import { Fragment, useEffect, useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";
import {
  ChevronDown,
  ChevronUp,
  Edit,
  ExternalLink,
  Ghost,
  MailCheck,
  MessageSquare,
  Clipboard,
} from "lucide-react";

import {
  commaSeparatedEnglishList,
  formatUSPhoneNumber,
} from "../../common/utils";
import { Advisor, AdvisorStatus, FamilyStub } from "protogen/common_pb";
import { getFormattedLocation } from "components/family/utils";

import { UserAvatar } from "components/common/CurrentUserAvatar";
import Collapse from "@mui/material/Collapse";

import MetadataSection from "../../components/common/MetadataSection";
import { UpdateAdvisorRequest } from "../../protogen/advisors_service_pb";
import { PROD_MATCHMAKING_FORM_UUID } from "components/documents/constants";
import { useUpdateAdvisor } from "../../services/advisor";
import Toggle from "components/common/Toggle";
import { PartialMessage } from "@bufbuild/protobuf";
import AdvisorAdminNotes from "./AdvisorAdminNotes";

const AdvisorMetadata = ({ advisor }: { advisor: Advisor }) => {
  const { request } = useUpdateAdvisor();
  if (advisor.metadata === undefined) return null;
  return (
    <Box
      sx={{
        marginTop: "24px",
        marginBottom: "12px",
      }}
    >
      <MetadataSection
        metadata={advisor.metadata}
        saveMetadata={async (metadata) => {
          await request(
            new UpdateAdvisorRequest({
              advisorRef: advisor.ref,
              shouldUpdateMetadata: true,
              updatedMetadata: metadata,
            }),
          );
        }}
        entityType={"advisor"}
      />
    </Box>
  );
};

const AdvisorSettings = ({ advisor }: { advisor: Advisor }) => {
  const [_advisor, setAdvisor] = useState(advisor);
  const { request, loading } = useUpdateAdvisor();
  const update = async (change: PartialMessage<UpdateAdvisorRequest>) => {
    if (loading) return;
    const resp = await request(
      new UpdateAdvisorRequest({
        advisorRef: _advisor.ref,
        ...change,
      }),
    );
    if (resp?.advisor) {
      setAdvisor(resp.advisor);
    }
  };
  useEffect(() => {
    setAdvisor(advisor);
  }, [advisor]);
  return (
    <Box sx={{ marginTop: "12px" }}>
      <Typography variant="h3" sx={{ marginBottom: "6px" }}>
        Advisor Settings
      </Typography>
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateEmailNotifications: true,
            emailNotificationsEnabled: e.target.checked,
          })
        }
        checked={_advisor.emailNotificationsEnabled}
        label="Email notifications"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateNewClientOpportunitiesEnabled: true,
            newClientOpportunitiesEnabled: e.target.checked,
          })
        }
        checked={_advisor.newClientOpportunitiesEnabled}
        label="New opportunities"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasCompletedOnboardingSession: true,
            hasCompletedOnboardingSession: e.target.checked,
          })
        }
        checked={_advisor.hasCompletedOnboardingSession}
        label="Onboarding completed"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasCompletedMatchmakingQuestionnaire: true,
            hasCompletedMatchmakingQuestionnaire: e.target.checked,
          })
        }
        checked={_advisor.hasCompletedMatchmakingQuestionnaire}
        label="Matchmaking questionnaire"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasSetupApp: true,
            hasSetupApp: e.target.checked,
          })
        }
        checked={_advisor.hasSetupApp}
        label="App installed"
      />
    </Box>
  );
};

const getStatus = (status: AdvisorStatus) => {
  switch (status) {
    case AdvisorStatus.ACTIVE:
      return <Chip variant="outlined" color="success" label={"Active"} />;
    case AdvisorStatus.PENDING:
      return <Chip variant="outlined" color="warning" label={"Pending"} />;
    case AdvisorStatus.DEACTIVATED:
      return <Chip variant="outlined" color="error" label={"Deactivated"} />;
    case AdvisorStatus.STAFF:
      return <Chip variant="outlined" label={"Staff"} />;
    case AdvisorStatus.TEST:
      return <Chip variant="outlined" label={"Test"} />;
    default:
      return <Chip variant="outlined" label={"?"} />;
  }
};

type AdvisorCardProps = {
  advisor: Advisor;
  forceOpen: boolean;
  onEdit: () => void;
  onClose: () => void;
  onOpen: () => void;
};

export default ({
  advisor,
  forceOpen,
  onEdit,
  onClose,
  onOpen,
}: AdvisorCardProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const byline =
    commaSeparatedEnglishList(advisor.specialties) || advisor.blurbText;
  const location = `${advisor.city}, ${advisor.state}`;
  const baseUrl = window.location.origin;
  const matchmakingFormUrl = `${baseUrl}/forms/complete/${PROD_MATCHMAKING_FORM_UUID}`;
  const [copied, setCopied] = useState(false);

  const toggleOpen = () => {
    if (open) {
      onClose();
    } else {
      onOpen();
    }
    setOpen(!open);
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="8px"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        background: "#FFF",
        width: "100%",
        padding: "16px 24px",
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap="12px"
        sx={{ cursor: !open ? "pointer" : undefined }}
        onClick={() => !open && toggleOpen()}
      >
        <Box flex={0}>
          <UserAvatar user={advisor} size={48} clickable={false} />
        </Box>
        <Box flex={5}>
          <Box display="flex" flexDirection={"column"} gap="4px">
            <LinkRouter to={`/advisor/${encodeURIComponent(advisor.ref)}`}>
              <Typography
                variant="bodyHeavy"
                color="#262626"
              >{`${advisor.firstName} ${advisor.lastName}`}</Typography>
            </LinkRouter>
            <Typography
              variant="body"
              color="text.secondary"
              sx={{
                textWrap: "none",
                textOverflow: "ellipses",
                overflow: "hidden",
              }}
            >
              {isMobile ? location : byline}
            </Typography>
          </Box>
        </Box>
        <Box flex={2} justifyContent="end" display={isMobile ? "none" : "flex"}>
          <Typography
            variant="body"
            color="#262626"
            sx={{
              textAlign: "right",
            }}
          >
            {location}
          </Typography>
        </Box>
        <Box flex={2} display={isMobile ? "none" : "flex"} justifyContent="end">
          {getStatus(advisor.status)}
        </Box>
        <Box flex={0}>
          <IconButton aria-label="expand row" size="small" onClick={toggleOpen}>
            {open ? <ChevronUp /> : <ChevronDown />}
          </IconButton>
        </Box>
      </Box>
      {isMobile && byline && (
        <Typography variant="bodySmall" color="#6B6E7B">
          {byline}
        </Typography>
      )}
      <Collapse in={open || forceOpen} timeout="auto" unmountOnExit>
        <div
          style={{
            marginTop: "12px",
            height: "1px",
            background: "#E2E3E4",
          }}
        />
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
            <Box sx={{ padding: "24px 32px" }} flex={2}>
              <Typography variant="h3" marginBottom="20px">
                Details
              </Typography>
              <Box display="flex" flexDirection="column" gap="10px">
                <Box>
                  <Typography variant="bodyHeavy">Faye email</Typography>
                  <Typography variant="body">{advisor.username}</Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Faye number</Typography>
                  <Typography variant="body">
                    {formatUSPhoneNumber(advisor.advisorPhone)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Families</Typography>
                  <Typography variant="body">
                    {advisor.families.length === 0 && <i>None</i>}
                    {advisor.families.map((f: FamilyStub, idx: number) => (
                      <Fragment key={f.ref}>
                        <LinkRouter
                          inline
                          to={`/families/${encodeURIComponent(f.ref)}`}
                          sx={{ color: "#198282" }}
                        >
                          {f.name}
                        </LinkRouter>
                        {idx < advisor.families.length - 1 ? ", " : ""}
                      </Fragment>
                    ))}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Personal email</Typography>
                  <Typography variant="body">
                    {advisor.personalEmail}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Personal phone</Typography>
                  <Typography variant="body">
                    {formatUSPhoneNumber(advisor.personalPhone)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Home address</Typography>
                  <Typography variant="body">
                    {getFormattedLocation(advisor.address)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Roles</Typography>
                  <Typography variant="body">
                    {advisor.roles.length === 0 && <i>None</i>}
                    {advisor.roles.map((r) => (
                      <Chip size="small" key={r} variant="outlined" label={r} />
                    ))}
                  </Typography>
                </Box>
              </Box>
              <AdvisorMetadata advisor={advisor} />
            </Box>
            <Box sx={{ padding: "24px 32px" }} flex={2}>
              <Typography variant="h3" marginBottom="20px">
                Actions
              </Typography>
              <Box display="flex" flexDirection="column">
                <Button
                  startIcon={<Edit />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={onEdit}
                >
                  Edit advisor
                </Button>
                <Button
                  startIcon={<MessageSquare />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigate(`/inbox/messages?recipientRefs=${advisor.ref}`);
                  }}
                >
                  Direct Message
                </Button>
                <Button
                  startIcon={<ExternalLink />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    window.open(advisor.profilePath, "_blank");
                  }}
                  disabled={!advisor.isProfilePublic}
                >
                  View public profile
                </Button>
                <Button
                  startIcon={<Ghost />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigate(
                      `/tools/impersonate-user?username=${encodeURIComponent(
                        advisor.username,
                      )}`,
                    );
                  }}
                >
                  Impersonate
                </Button>
                <Button
                  startIcon={<MailCheck />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigate(
                      `/tools/manage-roles?username=${encodeURIComponent(
                        advisor.username,
                      )}`,
                    );
                  }}
                >
                  Manage roles
                </Button>
                <Button
                  startIcon={<Clipboard />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigator.clipboard.writeText(matchmakingFormUrl);
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 2000);
                  }}
                >
                  {copied ? "Copied!" : "Copy matching form url to clipboard"}
                </Button>
                <AdvisorSettings advisor={advisor} />
              </Box>
            </Box>
          </Box>
          {(forceOpen || open) && <AdvisorAdminNotes advisor={advisor} />}
        </Box>
      </Collapse>
    </Box>
  );
};
